<script>
/* eslint no-multi-spaces: 0 */
/* eslint key-spacing    : 0 */

/**
 * Grid module
 * @module components/Grid
 *
 */

import { AgGridVue }      from 'ag-grid-vue'
import { Eved, EventBus } from '@/event-bus'
import { Model, Data, Filter }    from './adapters'

// plugins
import '@/core/exporter'
import ExportData         from './export-data'
import ColumnSelector     from './column-selector'
import CustomHeader       from './header'

// skins
import PerfectScrollbar   from 'perfect-scrollbar'
import 'perfect-scrollbar/css/perfect-scrollbar.css'
import '@/assets/sass/grid-theme.scss'

const ALL_ROWS_SELECTED = 1
const NO_ROWS_SELECTED = 0

const ROW_HEIGHT = 35
const HEADER_HEIGHT = 40
const EXPANDED_ROW_HEIGHT = 120
const BOTTOM_PADDING = 55

export default {
  props: {
    uid        : {
      type: String,
      default() {
        return `grid-${this._uid}`
      }
    },
    name       : String,  // grid name that shows in the rendered title and in search placeholders
    label      : String,  // text representation of the entity
    desc       : String,  // grid description that shows in the rendered subtitle
    alias      : String,  // name used for retrieving grid bound data from backend
    endpoint   : String,  // url to retrieve data from (@link module:core/api and @link module:core/api/map)
    endpointPayloadType: {
      type: String,
      default: 'form'
    },
    endpointMethod: {
      type: String,
      default: 'post'
    },
    permissions: {
      type     : Object,
      default  : function () { return {} }
    },
    screen     : String,  // screen/tab name to retrieve data from (@link module:core/api/map)
    model      : Object,  // column model (either passed or exported by refering/parent module)
    search     : Boolean, // grid has top search bar displayed or not
    searchPlaceholder: String, // Default Current View text and search placeholder
    tabs       : Array,   // additional tabs
    allowExport: {
      type     : Boolean, // allow data to be exported (@link module:components/Grid/export-data)
      default  : true
    },
    exportUri  : String,  // path to endpoint from where to retrieve export data
    views      : {
      type     : Boolean, // grid has multiple views (sets of filtered data) (@link module:components/Grid/views)
      default  : true
    },
    checkboxes : {
      type     : Boolean, // rows can be selected
      default  : true
    },
    hideSelectAll: {
      type     : Boolean  // hides select all
    },
    sorting    : {
      type     : Boolean, // columns have sorting enabled
      default  : true
    },
    pinning    : {
      type     : Boolean, // columns can be pinned
      default  : false
    },
    reordering : Boolean, // columns can be reordered
    resizing   : Boolean, // columns can be resized
    visibility : Boolean, // grid has column selector (@link module:components/Grid/column-selector)
    footer     : Boolean, // grid has footer
    legacy     : Boolean,
    layout: {
      type: String, // grid layout (page, frame,small, widget)
      default: function () {
        if (~this.$parent.$parent.$vnode.tag.indexOf('-widget')) {
          return 'widget'
        }
        return 'page'
      },
      validator: val => {
        return ['page', 'frame', 'small', 'widget'].indexOf(val) >= 0
      }
    },
    filter     : Object,   // initial filter
    onlyVisibleColumns: Boolean,
    defaultView: Object,   // default view (filter set)
    customViews: Boolean,  // allows to customize views
    sort       : Object,   // initial sort
    disableFilter: Boolean,
    data       : Array,    // row data
    actions    : Array,    // row actions
    autoHeight : Boolean,  // grid expands/contracts to fit rows
    fitColumns : Boolean,  // columns expand/contract to fit grid width

    dragRows   : Boolean,
    removeMany : Boolean,
    validateRemoveMany: {
      type: Function,
      default: undefined
    },
    cloneMany  : Boolean,
    validateCloneMany: {
      type: Function,
      default: undefined
    },
    undo       : Boolean,
    editable   : {         // this is true when all columns are editable
      type: Boolean,
      default: undefined
    },
    editFullRowOnAdd: Boolean, // this should be used when the grid is not editable at all (none of the colums is editable)
    totals     : Boolean,
    total      : [Number, String, Object],
    totalsData : { type: Object, default: () => ({}) },
    enableErrorMessage: Boolean,
    helptipHeader: String,
    helptipBody: String,
    currencyCode: String,

    rowDetailsComponent: Object,
    showColumnSelector: {
      type     : Boolean,
      default  : true
    },
    rowClasses : {
      type     : Object,
      default  : function () { return {} }
    },
    additionalFilters: {
      type: Array,
      default: function () { return [] }
    },
    canRemoveDefaultFilter: Boolean,
    resetNotifications: 0,
    canShowNotifications: Boolean,
    noTooltipFields: {
      type: Array,
      default: []
    }
  },

  computed: {
    Notifications() {
      return this.$notifications.state
    },
    getSize() {
      return this.data && this.autoHeight
        ? {
          width: '100%',
          'height': `${this.data &&
            (this.data.length + 1) * ROW_HEIGHT +
            HEADER_HEIGHT +
            BOTTOM_PADDING +
            this.expandedRows * EXPANDED_ROW_HEIGHT +
            this.expandOffset
          }px`,
          'min-height': '80px'
        }
        : {
          width: '100%',
          height: `${HEADER_HEIGHT + ROW_HEIGHT * 10.5}px` // '650px'
        }
    },

    quickSearchPlaceholder() {
      const t = this.$t
      return this.currentView
        ? `${t('Search')} ${this.currentView && this.currentView.Label}`
        : this.searchPlaceholder
          ? `${t('Search')} ${t(this.searchPlaceholder)}`
          : t('Search')
    },

    currency() {
      return this.currencyCode || this.$parent.currency
    },

    viewToBeSavedValid() {
      let matchSavedViews = this.savedViews.map(k => k.Label).filter(k => k === this.viewToBeSaved.label)
      return !matchSavedViews.length
    },

    backendSavedViews() {
      return this.savedViews.length > 1 && this.savedViews[0] && this.savedViews[0].FilterJson
    }
  },

  data() {
    return {
      /**
       * Used for top quick search across all columns (@link method:quickSearch)
       */
      noOfRecords: 0,
      shownDetails: (this.$store.state.gridExpandOffset || {})[this.uid] || false,
      shownPopover: false,
      loading: true,
      searchTerm: '',
      currentView: null,
      currentViewChanged: false,
      doNotShowOverlay: false,
      filters: [],
      savedViews: [],
      viewToBeSaved: {
        label: ''
      },
      allRowsSelected: null,
      selectedRows: [],
      unSelectedRows: [],
      updatedCells: [],
      expandedRows: 0,
      noOfExpandedRows: 0,
      expandOffset: (this.$store.state.gridExpandOffset || {})[this.uid] || 0,
      permissionsMix: Object.keys(this.permissions).length === 0 ? this.$user.$.claims || [] : this.permissions,
      totalsInvalid: false,
      canShowNotifications: true,
      pendingRequests: 0,
      cellToFocus: {}
    }
  },

  created() {
    this.initSavedViews()
    this.initModel()

    EventBus.$on(Eved.GENERIC.LANGUAGE_CHANGED, this.updateLocales)

    this.$on('redraw', () => this.ps.update())

    this.$on('expandedRow', this.expandedRow)
  },

  updated() {
    this.loading = false
  },

  beforeMount() {
    this.initGrid()
  },

  beforeDestroy() {
    this.$notifications.removeAll()
  },

  mounted() {
    this.initScrollbar()
    this.attachSelectAllRows()
    this.attachActions()
    this.initDragDrop()

    this.initToggleButton()
  },

  methods: {
    noResults() {
      return this.gridOptions.api && this.gridOptions.api.rowRenderer.lastRenderedRow < 0
    },

    validateCells() {
      let invalidCell = this.$el.querySelector('.ag-custom-cell.invalid-amount')
      invalidCell && invalidCell.__vue__.notify(true)

      return invalidCell
    },

    initToggleButton() {
      let button = this.$el.querySelector('.actions-bar [toggle]') && this.$el.querySelector('.actions-bar [toggle]').__vue__
      if (button) {
        let columns = button.$attrs.toggle
        let text = this.toggleButtonText || button.$el.getAttribute('data-toggle-text') || button.$el.innerText

        this.toggledColumns = columns
        this.toggleButtonText = text
        let toggleTextInfo = {
          button: button,
          text: text,
          columns: columns
        }
        button.$el.setAttribute('data-toggle-text', button.$el.innerText)
        this.toggleText(toggleTextInfo)

        button.$el.onclick = (event) => {
          this.toggleColumns(columns)
          this.toggleText(toggleTextInfo)
        }
      }
    },
    toggleText(toggleTextInfo) {
      let visible = this.toggleColumns(toggleTextInfo.columns, null)
      toggleTextInfo.button.$el.innerText = visible
        ? `Hide ${toggleTextInfo.text}`
        : `Show ${toggleTextInfo.text}`
    },
    /**
     * Get any draft data from storage
     *
     */
    checkDraftData() {
      if (this.checkDraftData.alreadyChecked || !this.gridOptions.datasource.loaded) return

      let grid = this

      if (
        localStorage.getItem('evedDraftData') &&
        JSON.parse(localStorage.getItem('evedDraftData')).draft === this.endpoint
      ) {
        //
        this.$notifications.notify({
          type: 'plain',
          icon: 'exclamation-circle',
          placement: { from: 'top', align: 'center' },
          timeout: 0,
          message: `${this.$t('You have unsaved data. Restore ?')}`,
          dismiss: this.$t('Yes'),
          goto: null,
          onClose: function (dismiss) {
            if (dismiss) {
              grid.restoreDraft()
            } else if (dismiss === null) {
              localStorage.removeItem('evedDraftData')
            }
          }
        })
        // don't check again
        this.checkDraftData.alreadyChecked = true
      }
    },

    restoreDraft() {
      let data = JSON.parse(localStorage.getItem('evedDraftData')).data

      data && this.gridOptions.api.forEachNode((node) => {
        if (data[node.rowIndex]) {
          data[node.rowIndex].data.draft = true
          node.setData(data[node.rowIndex].data)
        }
      })
      this.keepRestoringDraft = true
    },

    /**
     * Get saved views from backend
     *
     */
    initSavedViews() {
      if (this.alias) {
        this.loading = true
        this.api.forms.post(this.api.$.common.filterViews, {
          action: 'list',
          Alias: this.alias
        }).then(r => {
          this.loading = false
          if (r && r.data.Data) {
            let defaultView = null

            this.defViewIndex = 0
            this.savedViews = r.data.Data.length ? r.data.Data : [{}]

            this.savedViews.map((k, i) => {
              k.Label === '~last.Search=' && (this.defViewIndex = i)
              if (k.IsDefault) {
                defaultView = k
              }
            })

            this.savedViews[this.defViewIndex].Label = this.label || 'All ' + (this.searchPlaceholder || this.name)
            this.savedViews[this.defViewIndex].icon = 'fas fa-eye'

            if (this.defaultView) {
              this.defViewIndex++
              this.savedViews.splice(0, 0, {
                Label: this.defaultView.label,
                FilterJson: JSON.stringify(this.defaultView.filter),
                icon: this.defaultView.icon
              })
            }

            if (defaultView) {
              this.currentView = defaultView
              this.currentView.icon = this.defaultView.icon
              this.savedViews[0].icon = null
            } else {
              this.currentView = this.savedViews[0]
            }

            if (this.filter) {
              this.gridOptions.api.setFilterModel(this.filter)
              this.isInitialFilter = true
            } else {
              this.applySavedFilter(this.currentView)
            }

            if (this.sort) {
              this.gridOptions.api.setSortModel(Model.sort(this.sort))
            }
          } else {
            // Notification message
            // (probably session expired)
            this.$store.commit('updateSessionValidity', null)
          }
        })
      } else {
        this.loading = false
      }
    },

    /**
     * Apply saved view (filter)
     *
     */
    applySavedFilter(filter) {
      this.currentViewChanged = true
      if (this.gridOptions.api) {
        this.gridOptions.api.setFilterModel(null)
        this.gridOptions.api.setFilterModel(
          Filter.mixout.call(this, filter.FilterJson, this.$parent.model || this.model)
        )

        let columnsToShow = filter.FilterJson && JSON.parse(filter.FilterJson).ColumnsToShow
        if (columnsToShow) {
          this.$nextTick(() => {
            this.gridOptions.columnApi.getAllColumns().map(column => {
              if (column.colDef.headerName !== '_') {
                let visible = columnsToShow.indexOf(column.colId) > -1
                this.gridOptions.columnApi.setColumnVisible(column.colId, visible)
                this.$refs.columnSelector.toggleColumn(column, visible)
              }
            })
          })
        }
      }
    },

    /**
     * Save a view (filter)
     *
     */
    saveView() {
      this.api.forms.post(this.api.$.common.filterViews, {
        action: 'save',
        UGFID: 0,
        Label: this.viewToBeSaved.label,
        Alias: this.alias,
        FilterJson: JSON.stringify(this.getCurrentView()),
        SortJson: '{}'
      }).then(r => {
        this.savedViews.push({
          UGFID: r.data.UGFID,
          Label: this.viewToBeSaved.label,
          FilterJson: JSON.stringify(this.getCurrentView())
        })
        this.currentView = {
          Label: this.viewToBeSaved.label
        }
        this.viewToBeSaved = { label: '', FilterJson: '{}' }

        this.$refs.currentViewList.deactivate()
      })
    },

    /**
     * Update a view (filter)
     *
     */
    updateView() {
      this.api.forms.post(this.api.$.common.filterViews, {
        action: 'save',
        UGFID: this.currentView.UGFID,
        Label: '',
        Alias: this.alias,
        FilterJson: JSON.stringify(this.getCurrentView()),
        SortJson: '{}'
      }).then(r => {
        //
        this.$refs.currentViewList.deactivate()
      })
    },

    /**
     * Set a view as default (filter)
     *
     */
    setDefaultView(view) {
      if (!this.customViews) return
      this.currentView.icon = null

      this.api.forms.post(this.api.$.common.filterViews, {
        action: 'setDefault',
        UGFID: view.UGFID,
        Alias: this.alias
      }).then(r => {
        this.currentView.endpoint = null
        this.currentView = this.savedViews[this.savedViews.indexOf(view)]
        this.currentView.icon = this.defaultView.icon
        // this.applySavedFilter(this.currentView)

        this.$refs.currentViewList.deactivate()
      })
    },

    /**
     * Remove a view (filter)
     *
     */
    removeView(view) {
      this.api.forms.post(this.api.$.common.filterViews, {
        action: 'delete',
        UGFID: view.UGFID,
        Alias: this.alias
      }).then(r => {
        this.savedViews.splice(this.savedViews.indexOf(view), 1)
        this.currentView = this.savedViews[0]
        // this.applySavedFilter(this.savedViews[0])

        this.$refs.currentViewList.deactivate()
      })
    },

    /**
     * Get the filters and/or columns
     *
     */
    getCurrentView() {
      let filters = Filter.mixin(this.gridOptions.api.getFilterModel())
      let columns = this.gridOptions.columnApi.getAllDisplayedColumns()
                      .filter(column => column.colDef.headerName !== '_')
                      .map(column => column.colId)

      return {
        ...filters,
        ColumnsToShow: columns
      }
    },

    /**
     * Maps backend model (exported by refering/parent module) to column model
     *
     */
    initModel() {
      const model = this.$parent.model || this.model || {}
      const actions = this.$parent.actions || this.actions || []

      this.columnDefs = Model.mixin(model, actions, this)

      // default column
      this.defaultColumn = this.model ? Object.keys(this.model)[1] : this.columnDefs[1].field
    },

    /**
     * Binds grid options and endpoint
     *
     */
    initGrid() {
      const grid = this

      this.gridOptions = {
        context                   : this,
        headerHeight              : HEADER_HEIGHT, // header height (px)
        rowHeight                 : ROW_HEIGHT,    // row height (px)
        getRowHeight: (p) => {
          p.node.expanded ? this.noOfExpandedRows++ : this.noOfExpandedRows--
          if (p.node.expanded) return EXPANDED_ROW_HEIGHT
          return p.node.rowPinned ? HEADER_HEIGHT : ROW_HEIGHT
        },

        rowClassRules             : this.rowClass(this),
        defaultColDef : {
          suppressMovable         : !this.reordering,
          headerComponentFramework: CustomHeader,
          // enableCellChangeFlash   : true,
          unSortIcon              : true,                  // always show sort icon
          checkboxSelection       : this.checkboxes
                                    ? this.isFirstColumn   // enable checkbox row selector
                                    : false
          /**
           * Selecting all rows is not supported for virtual scrolling
           * (@link method:attachSelectAllRows)
           */
          // headerCheckboxSelection : this.isFirstColumn
        },
        editType: this.editFullRowOnAdd ? 'fullRow' : undefined,
        singleClickEdit: this.editable !== false,
        stopEditingWhenGridLosesFocus: true,
        processRowPostCreate      : (p) => this.processRow(p, this),

        columnDefs                : this.columnDefs,       // column model

        pinning                   : this.pinning,

        // suppress animations for moving/pinning columns (avoids flicker in IE)
        // suppressColumnMoveAnimation: true,
        // suppressAnimationFrame: true,

        enableFilter              : true,                  // enable column filtering ui
        rowModelType              : this.data ? null : 'infinite',            // enable virtual scrolling
        rowData                   : this.data,
        pinnedBottomRowData       : this.totals ? this.getTotals() : null,
        maxBlocksInCache          : 2,                     // cache only 2 sets of data(pages)
        cacheBlockSize            : 100,                   // default of 100 rows per set of data(page)
        enableServerSideSorting   : !this.data,                  // sorting on backend
        enableServerSideFilter    : !this.data,                 // filtering on backend
        enableSorting             : this.sorting,
        sortingOrder              : ['asc', 'desc'],
        datasource                : this.data ? null : Data.fetch(this),      // fetch data from backend

        rowSelection              : 'multiple',            // selectable rows
        rowDeselection            : false,
        suppressRowClickSelection : true,
        // onSelectionChanged        : this.onSelectRow,
        onRowSelected             : this.onSelectRow,

        onDisplayedColumnsChanged: this.onColumnsVisibleChanged,
        onColumnVisible           : this.onColumnVisibilityChanged,
        // onVirtualColumnsChanged   : this.onColumnsDisplayedChanged,

        onFilterChanged           : this.handleFilters,    // filter callbacks for external filtering
        onModelUpdated            : this.data || this.onDataLoaded,     // data loaded callback

        postProcessPopup          : this.handlePopups,     // popups behavior

        onCellEditingStarted      : this.handleCellEdit,
        onCellValueChanged        : this.handleInlineEdit, // inline editing
        onRowValueChanged         : this.handleInlineEditRow, // inline editing

        overlayNoRowsTemplate     : `<div>
                                      <i class="icon far fa-times-circle"></i>
                                      <p t-key="Nothing to show">${this.$t('Nothing to show')}</p>
                                    </div>`,
        onBodyScroll              : this.onViewportChanged,
        onGridReady               : this.fitColumns &&
                                    function(params) {
                                      grid.autoFitColumns()
                                      // grid.updatePs()
                                    }
      }
    },

    autoFitColumns() {
      const visibleColumns = this.$refs.columnSelector
        ? this.$refs.columnSelector.columnsList.filter(
            k => !k.hide && k.headerName !== '_'
          ).length
        : 0
      this.fitColumns &&
        visibleColumns <= 10 &&
        this.gridOptions.api && this.gridOptions.api.sizeColumnsToFit()
      this.updatePs()
    },

    updatePs() {
      this.$nextTick(() => this.ps.update())
    },

    refreshGrid() {
      this.quickSearch()
      this.onSelectAll(false)
    },

    /**
     * Refresh cache to trigger datasource reload with new @link data:searchTerm
     *
     */
    quickSearch() {
      const quickFilter = {
        key: 'quickSearch',
        label: this.searchTerm
      }

      if (this.data) {
        this.gridOptions.api.setQuickFilter(this.searchTerm)
      } else {
        this.gridOptions.api.refreshInfiniteCache()
      }

      const quickFilterIndex = this.filters.findIndex(filter => filter.key === quickFilter.key)

      // replace the quick filter in the filters bar
      if (this.searchTerm) {
        ~quickFilterIndex && this.filters.splice(quickFilterIndex, 1)
        this.filters.push(quickFilter)
      } else {
        ~quickFilterIndex && this.filters.splice(quickFilterIndex, 1)
      }
    },

    isFirstColumn(params) {
      const displayedColumns = params.columnApi.getAllDisplayedColumns()
      const firstKey = this.dragRows ? 1 : 0
      const thisIsFirstColumn = displayedColumns[firstKey] === params.column

      return thisIsFirstColumn
    },

    onColumnVisibilityChanged(params) {
      this.initToggleButton()

      const columnHeaderElement = params.column && this.$el.querySelector('[col-id="' + params.column.colId + '"]')
      if (!columnHeaderElement) return

      // update select all state
      if (this.allRowsSelected) {
        columnHeaderElement.querySelector('.ag-checkbox-checked').classList.remove('ag-hidden')
        columnHeaderElement.querySelector('.ag-checkbox-unchecked').classList.add('ag-hidden')
      }

      //
      this.fitColumns && this.gridOptions.api.sizeColumnsToFit()
    },

    onColumnsVisibleChanged() {
      this.autoFitColumns()
    },

    onColumnsDisplayedChanged(params) {
      //
    },

    toggleColumns(columns, check) {
      let visible = true

      for (let column of columns) {
        if (this.gridOptions.columnApi.getColumn(column) && !this.gridOptions.columnApi.getColumn(column).visible) {
          visible = false
          break
        }
      }

      if (check !== null) {
        this.gridOptions.columnApi.setColumnsVisible(columns, !visible)
      } else {
        //
      }

      return visible
    },

    onSelectAll(checked) {
      // select/deselect all rows and persist selections
      this.allRowsSelected = checked
      this.restoreRowSelections(checked)

      // reset selection
      if (!checked) {
        this.selectedRows = []
        this.unSelectedRows = []
        this.allRowsSelected = this.gridOptions.api.getSelectedRows().length === 0 ? null : this.allRowsSelected
        //
        EventBus.$emit(Eved.GENERIC.UNSELECTED_ALL)
        this.$emit('selectedRows', false)
      } else {
        EventBus.$emit(Eved.GENERIC.SELECTED_ALL)
        this.$emit('selectedRows', true)

        // redraw totals
        if (this.gridOptions && this.gridOptions.api && this.totals && this.allRowsSelected) {
          this.gridOptions.api.redrawRows({
            rowNodes: [this.gridOptions.api.getPinnedBottomRow(0)]
          })
        }
      }
    },

    onSelectRow(event) {
      let selected = this.gridOptions && this.gridOptions.api && this.gridOptions.api.getSelectedNodes()
      this.$emit('selectedRows', selected)

      // redraw totals
      if (this.gridOptions && this.gridOptions.api && this.totals && this.selectedRows !== ALL_ROWS_SELECTED && this.selectedRows !== NO_ROWS_SELECTED) {
        this.gridOptions.api.redrawRows({
          rowNodes: [this.gridOptions.api.getPinnedBottomRow(0)]
        })
      }

      // emit UNSELECTED_ALL if unselecting last selected row
      if (selected && !selected.length) {
        this.onSelectAll(false)
      }

      if (
          (this.selectedRows === ALL_ROWS_SELECTED || this.selectedRows === NO_ROWS_SELECTED) &&
          event.node.selected && this.allRowsSelected ||
          !event.node.selected && this.allRowsSelected === false ||
          !event.node.selected && this.allRowsSelected === null && !this.selectedRows.length
      ) return

      if (event.node.selected) {
        // remove any selected rows from the unselected list
        this.unSelectedRows.length && this.unSelectedRows.splice(this.unSelectedRows.indexOf(event.node.data), 1)
        //
      } else {
        // add unselected rows to the unselected list
        this.selectedRows === ALL_ROWS_SELECTED ? this.unSelectedRows.push(event.node.data) : null
        this.allRowsSelected = false
        //
      }

      // reset selected rows list
      this.selectedRows = this.allRowsSelected === true ? ALL_ROWS_SELECTED
                          : this.allRowsSelected === false ? NO_ROWS_SELECTED
                          : (this.gridOptions.api ? this.gridOptions.api.getSelectedRows() : NO_ROWS_SELECTED)

      if (this.selectedRows === NO_ROWS_SELECTED) {
        this.selectedRows = []
      }

      //
      this.selectedRows.length && EventBus.$emit(Eved.GENERIC.SELECTED_ITEM, this.selectedRows)
      this.unSelectedRows.length && EventBus.$emit(Eved.GENERIC.UNSELECTED_ITEM, this.unSelectedRows)
    },

    onDataLoaded() {
      // get draft
      this.checkDraftData()
      this.keepRestoringDraft && this.restoreDraft()

      // this.restoreRowSelections(this.allRowsSelected)

      /* !this.viewportReady && */ this.autoFitColumns()
      if (!this.value) {
        this.noOfRecords = this.gridOptions.api.rowModel.infiniteCache.virtualRowCount
      }

      // adding new row
      if (this.addedRow) {
        this.addRow()
        delete this.addedRow
      }
    },

    /**
     * Restores selected rows
     *
     */
    restoreRowSelections(checked) {
      this.gridOptions.api.forEachNode((node) => {
        node.setSelected(checked)
      })
    },

    /**
     * Handle popups behavior
     *
     */
    handlePopups(p) {
      let filterInstance = p.column.gridApi.getFilterInstance(p.column.colDef.field).component.$refs.input

      // reset auto filter
      p.column.colDef.auto = false

      // hook transitions
      if (p.column.colDef.closeFilter) {
        p.ePopup.className = 'ag-menu fade out'
        p.ePopup.style.display = 'none'
        p.column.colDef.closeFilter = false
      } else {
        p.ePopup.className = 'ag-menu fade in'
      }

      // autofocus search input
      if (filterInstance.$refs.input) {
        filterInstance.$refs.input.focus()
      }

      // ref to column filter trigger for filter editing
      p.column.colDef.menuTrigger = p.eventSource

      // FIXME: (hide other stuck filter components)
      setTimeout(() => {
        global.jQuery('.ag-menu.fade.in').length > 1 && global.jQuery('.ag-menu.fade.in:first').remove()
      }, 100)
    },

    /**
     * Handle filters from the top filters bar
     *
     */
    handleFilters(e) {
      const appliedFilters = e.api.getFilterModel()
      const filters = []
      const quickFilter = {
        key: 'quickSearch',
        label: this.searchTerm
      }

      if (this.data && this.gridOptions.api.filterManager.quickFilter) {
        filters[0] = quickFilter
      }

      Object.keys(appliedFilters).forEach(filter => {
        let filterLabel = appliedFilters[filter].filter
        let formattedLabel = appliedFilters[filter].formattedFilter
        const column = this.gridOptions.columnApi.getColumn(filter)

        if (filterLabel || formattedLabel) {
          if (column.colDef.source) {
            filterLabel = (formattedLabel || filterLabel).replace(/_/g, ', ')
          } else {
            filterLabel = (formattedLabel || filterLabel).replace(/_/g, ' - ')
          }
        }

        if (appliedFilters[filter].filter && column.visible) {
          // ~this.additionalFilters.indexOf(filter) ||
          filters.push({
            key: filter,
            label: filterLabel,
            column: column.colDef.headerName
          })
        }

        if (this.searchTerm) {
          filters.push(quickFilter)
        }
      })

      this.filters = utils.copy(filters)

      // hide filter
      setTimeout(() => {
        let lastFilter = filters.pop()
        lastFilter = lastFilter ? lastFilter.key : null

        const lastFilteredColumn = this.gridOptions.columnApi && this.gridOptions.columnApi.getColumn(lastFilter)
                                    ? this.gridOptions.columnApi.getColumn(lastFilter).colDef
                                    : null

        if (lastFilter && lastFilteredColumn) {
          if (!this.isInitialFilter) {
            lastFilteredColumn.closeFilter = true
            lastFilteredColumn.menuTrigger && lastFilteredColumn.menuTrigger.click()
          }
          this.isInitialFilter = false
        }
      }, 500)
    },

    /**
     * Edit a filter (trigger corresponding filter)
     *
     */
    editFilter(filter) {
      if (filter.key === 'quickSearch') {
        const input = this.$refs.quickSearch.$refs.input
        input.focus()
        input.setSelectionRange(0, input.value.length)
      } else {
        this.gridOptions.api.ensureColumnVisible(filter.key)
        setTimeout(() => {
          this.$refs.grid.$el.querySelector(`[col-id="${filter.key}"] .header-label`).click()
        }, 150)
      }
    },

    /**
     * Remove individual filters
     *
     */
    removeFilter(filter) {
      // remove the quick search filter
      if (filter.key === 'quickSearch') {
        this.filters.splice(this.filters.indexOf(filter), 1)
        this.searchTerm = ''
        this.quickSearch()
      } else {
        const filterComponent = this.gridOptions.api.getFilterInstance(filter.key)
        // remove column filter
        filterComponent.setModel(null)

        // apply filters
        this.gridOptions.api.onFilterChanged()

        // remove from list
        this.filters.splice(this.filters.indexOf(filter), 1)

        // reset current view
        if (!this.filters.length) {
          this.currentView = this.savedViews[this.defViewIndex || 1]
        }
      }
    },

    /**
     * Remove all filters
     *
     */
    removeFilters() {
      // reset current view
      this.currentView = this.savedViews[this.defViewIndex || 1]

      // reset any url filters
      this.$router.push({
        query: {
          filter: undefined
        }
      })

      // reset quick search
      this.searchTerm = ''
      this.gridOptions.api.setQuickFilter(null)

      this.gridOptions.api.setFilterModel(null)
      if (this.filter && !this.canRemoveDefaultFilter) {
        this.gridOptions.api.setFilterModel(this.filter)
        this.doNotShowOverlay = true
      }
    },

    /**
     * Cell editing
     *
     */
    handleCellEdit(p) {
      const row = p.rowIndex
      const column = p.column.colId
      const cell = p.api.rowRenderer.rowCompsByIndex[row].cellComps[column]
      const inputElement = cell.cellEditor.eHtmlElement

      const maxLength = p.column.colDef.maxLength

      maxLength && inputElement.setAttribute('maxlength', maxLength)

      const decimals = p.column.colDef.decimals
      if (decimals) {
        inputElement.oninput = function (e) {
          const value = e.target.value
          let number = utils.numeric.getNumberFromString(value)
          number = utils.numeric.setMaxNumberOfDigits(number, 2)
          inputElement.value = number
        }
      }

      this.cellToFocus = { row, column }
    },

    /**
     * Inline editing
     *
     */
    handleInlineEdit(p) {
      if (this.editFullRowOnAdd) {
        return
      }

      if (p.newValue !== p.oldValue && Number(p.newValue) !== p.oldValue) {
        /* this.updatedCells[p.rowIndex] = {
          initialValue: p.oldValue,
          value: p.newValue,
          data: p.data
        }
        this.$store.state.pendingData = {
          draft: this.endpoint,
          endpoint: this.endpoint ? this.endpoint.replace('Get', 'Save') : '',
          data: this.updatedCells
        } */
        const row = this.gridOptions.api.getDisplayedRowAtIndex(p.rowIndex)

        const success = (meta) => this.editedRow({...p, response: meta}, typeof meta === 'boolean' ? meta : true)
        const failure = (meta) => this.editedRow(p, meta)
        const done = () => {
          setTimeout(() => {
            this.$parent.$children[0].gridOptions.api.startEditingCell({
              rowIndex: this.cellToFocus.row,
              colKey: this.cellToFocus.column
            })
          })
        }

        this.$emit('changeRow',
          {
            index: p.rowIndex,
            data: p.data,
            oldValue: p.oldValue,
            columnId: p.column.colId
          }, success, failure, done)

        row.alreadyEdited = p.data._invalid
      }
    },

    /**
     * Inline editing row
     *
     */
    handleInlineEditRow(p) {
      const success = (meta) => this.editedRow({...p, response: meta}, typeof meta === 'boolean' ? meta : true)
      const failure = (meta) => this.editedRow(p, meta)

      if ((p.data.draft || p.data._new) && !p.node.alreadyEdited) {
        this.$emit('changeRow', { index: p.rowIndex, data: p.data }, success, failure)
      }
      p.data._invalid = false
      p.node.setData(p.data)
    },

    updateLocales() {
      let grid = this

      document.querySelectorAll('[t-key]').forEach(function (el) {
        el.innerHTML = grid.$t(el.getAttribute('t-key'))
      })
    },

    /**
     * Bind PerfectScrollbar to viewport
     *
     */
    initScrollbar() {
      const viewport = this.$el.querySelector('.ag-body-viewport')
      const actionsViewport = this.$el.querySelector('.ag-pinned-right-cols-viewport')

      this.ps = new PerfectScrollbar(viewport, {
        minScrollbarLength: 20,
        mouseWheelSpeed: 30
      })

      this.actionsPs = new PerfectScrollbar(actionsViewport, {
        minScrollbarLength: 20,
        mouseWheelSpeed: 30
      })

      /* viewport.addEventListener('wheel', e => {
        actionsViewport.scrollTop += e.deltaY
        e.preventDefault()
      }) */
    },

    initDragDrop() {
      //
      if (!this.dragRows) return
      const viewport = this.$el.querySelector('.ag-pinned-left-cols-viewport')

      let rowClone      = null
      let rowFrom       = null
      let dragFromIndex = null
      let grid          = this

      viewport.addEventListener('dragstart', e => {
        let id = e.target.getAttribute('comp-id')
        let row = this.$el.querySelector(`.ag-body-viewport [comp-id="${id}"]`)
        // rowClone = e.target.cloneNode(true)
        rowClone = row.cloneNode(true)
        rowClone.classList.add('row-ghost')
        rowClone.style.width = `${row.clientWidth}px`

        rowFrom  = e.target

        rowClone.classList.add('ag-row-dragging')
        // rowClone.style.visibility = 'visible'
        document.body.appendChild(rowClone)

        e.dataTransfer.setDragImage && e.dataTransfer.setDragImage(rowClone, 0, 0)
        e.dataTransfer.setData('text/plain', rowClone)
        e.target.classList.add('drag-over')

        dragFromIndex = rowFrom.getAttribute('row-index')
      })

      viewport.addEventListener('dragover', e => {
        let id = e.target.parentNode.getAttribute('comp-id')
        e.preventDefault()
        e.stopPropagation()
        // e.target.parentNode.classList.add('drag-enter')
        this.$el.querySelectorAll(`[comp-id="${id}"]`).forEach(el => el.classList.add('drag-enter'))
      })

      viewport.addEventListener('dragleave', e => {
        let id = e.target.parentNode.getAttribute('comp-id')
        e.preventDefault()
        e.stopPropagation()
        // e.target.parentNode.classList.remove('drag-enter')
        this.$el.querySelectorAll(`[comp-id="${id}"]`).forEach(el => el.classList.remove('drag-enter'))
      })

      viewport.addEventListener('dragend', e => {
        e.preventDefault()
        e.stopPropagation()
        dragFromIndex = null

        // rowClone.parentNode.removeChild(rowClone)
      })

      viewport.addEventListener('drop', e => {
        let id = e.target.parentNode.getAttribute('comp-id')
        e.preventDefault()
        e.stopPropagation()

        const node = e.target.parentNode
        grid.onDropRow(dragFromIndex, node.getAttribute('row-index'))

        rowFrom.classList.remove('drag-over')
        // node.classList.remove('drag-enter')
        this.$el.querySelectorAll(`[comp-id="${id}"]`).forEach(el => el.classList.remove('drag-enter'))
      })
    },

    onViewportChanged(e) {
      if (!this.viewportReady) {
        this.ps.update()
        this.actionsPs.update()
      }
      if (e.left === -1) this.viewportReady = true
    },

    /**
     * Enables selecting all rows
     *
     */
    attachSelectAllRows() {
      this.$el.querySelector('.ag-header').onclick = (event) => {
        if (~event.target.className.indexOf('-checkbox-')) {
          const toggle = event.target.className.indexOf('unchecked') > -1
          this.onSelectAll(toggle)
        }
      }
    },

    /**
     * Enables row actions
     *
     */
    attachActions() {
      this.$el.querySelector('.ag-root').onclick = (event) => {
        const action = event.target.getAttribute('data-action')
        const id     = event.target.getAttribute('data-id')
        const key    = event.target.getAttribute('data-key')
        const index  = event.target.getAttribute('data-index')

        action && this[`${action}Row`] && this[`${action}Row`](id, index, key)
      }
    },

    updateRowData(rowData) {
      this.gridOptions.api.setRowData(rowData || this.data)
    },

    expandRow(id, index, key) {
      let row = this.gridOptions.api.getDisplayedRowAtIndex(+index)

      row.expanded = !row.expanded
      row.data._expanded = row.expanded
      row.setData(row.data)

      this.$el.querySelector('.ag-floating-bottom').classList.add('animated')
      this.$el.querySelector('.ag-floating-bottom').style.opacity = '0'

      row.setRowHeight(row.expanded ? EXPANDED_ROW_HEIGHT + ROW_HEIGHT : ROW_HEIGHT)
      // row.expanded ? this.expandedRows++ : this.expandedRows--

      this.$nextTick(() => {
        this.shownDetails = this.noOfExpandedRows // this.expandedRows
        this.gridOptions.api.onRowHeightChanged()

        this.$el.querySelector('.ag-floating-bottom').style.cssText = ''
        this.$el.querySelector('.ag-floating-bottom').classList.add('fadeIn')
      })

      // trigger default details
      let expandAction = this.gridOptions.api.rowRenderer.rowCompsByIndex[+index].eBodyRow.querySelector('[col-id="details"]').querySelector('.ag-row-expand-icon')
      expandAction && expandAction.click()
    },

    expandedRow(p) {
      // collapse all other rows
      if (!this.data) {
        this.gridOptions.api.forEachNode(node => {
          node.expanded = false
          node.data._expanded = false
          node.setData(node.data)
          node.setRowHeight(ROW_HEIGHT)
        })
      }

      let row = this.gridOptions.api.getDisplayedRowAtIndex(p.index)

      row.expanded = p.expanded
      row.setRowHeight(this.gridOptions.rowHeight + (row.expanded ? p.height : 0))
      this.expandOffset = this.expandOffset + (p.reset ? 0 : p.height) * (row.expanded ? 1 : -1)

      this.$store.state.gridExpandOffset = this.$store.state.gridExpandOffset || {}
      this.$store.state.gridExpandOffset[this.uid] = this.expandOffset

      this.gridOptions.api.onRowHeightChanged()
    },

    addRow(data) {
      if (!this.gridOptions.api.getDisplayedRowCount()) {
        this.data = [data]
        this.gridOptions.api.refreshInfiniteCache()
      }

      let row = this.gridOptions.api.getDisplayedRowAtIndex(0)

      if (row) {
        this.gridOptions.api.insertItemsAtIndex(0, [this.addedRow || data])
        row = this.gridOptions.api.getDisplayedRowAtIndex(0)

        row.data._new = true
        row.setData(row.data)

        setTimeout(() => {
          this.gridOptions.api.startEditingCell({
            rowIndex : 0,
            colKey   : this.defaultColumn
          })
        })

        this.$emit('addRow')
      } else {
        this.gridOptions.api.ensureIndexVisible(0)
        this.addedRow = data
      }
    },

    cloneRow(data, index, key) {
      const grid = this
      const success = () => grid.gridOptions.api.refreshInfiniteCache()

      if (!index) {
        this.gridOptions.api.forEachNode(node => {
          if (node.selected) {
            index = Number(node.id)
          }
        })
      }

      let row = this.gridOptions.api.getDisplayedRowAtIndex(+index)
      let rowCopy = utils.copy(row.data)
      rowCopy._expanded = false

      this.gridOptions.api.insertItemsAtIndex(++index, [rowCopy])

      row = this.gridOptions.api.getDisplayedRowAtIndex(index)
      row.data._new = true

      row.setData(row.data)

      this.$emit('cloneRow', index, success)
    },

    cloneRows() {
      let toBeCloned = []
      const grid = this
      this.gridOptions.api.forEachNode(node => {
        node.selected && toBeCloned.push(node)
      })

      let errmsg = ''
      if (typeof this.validateCloneMany !== 'undefined') errmsg = this.validateCloneMany(toBeCloned)

      if (errmsg != null && errmsg !== '') {
        this.$notifications.notify({
          type: 'plain',
          icon: 'exclamation-circle',
          placement: { from: 'top', align: 'center' },
          message: errmsg,
          dismiss: 'OK',
          timeout: 0
        })
      } else {
        const success = () => grid.gridOptions.api.refreshInfiniteCache()

        grid.$emit('cloneRows', { rows: toBeCloned }, success)
      }
    },

    editRow(data, index, key) {
      const row = this.gridOptions.api.getDisplayedRowAtIndex(index)

      // row.data._new  = true
      // row.data.draft = true
      row._editing = true
      // row.setData(row.data)

      this.editable === true && setTimeout(() => {
        this.gridOptions.api.startEditingCell({
          rowIndex : +index,
          colKey   : key || this.defaultColumn
        })
      })

      this.$emit('editRow', { index, data: row.data })

      //
      // this.$router.push({ query: {wa: 'details'} })
    },

    editedRow(payload = {}, meta) {
      const row = this.gridOptions.api.getDisplayedRowAtIndex(payload.rowIndex)
      let errors = payload.response &&
                    payload.response.data &&
                    payload.response.data.Errors || []
      if (errors && errors.length > 0 && payload.response.status === 200) {
        meta = false
        global.app.$notifications.notify({
          type: 'plain',
          icon: 'times-octagon',
          message:
            `Error
            <br><strong>${errors[0].Message || 'Something went wrong'}</strong>
            <br><a class="btn btn-xs btn-actionbar" target="_blank" href="https://www.eved.com/contact-us/">Report this</a>`,
          timeout: 0,
          placement: { from: 'top', align: 'center' }
        })
      }
      if (meta === true) {
        // mark as saved and remove draft/new flag
        row.data._saved = true
        row.data._invalid = false

        row.data._new = false
        row.data.draft = false

        let model = payload.response &&
                    payload.response.data &&
                    payload.response.data.Data || {}

        // set ids
        row.data = {
          ...row.data,
          ...model
        }

        row.setData(row.data)

        this.gridOptions.api.refreshCells({
          rowNodes: [row],
          force: true
        })

        // remove saved flag after 5 sec.
        setTimeout(() => {
          row.data._saved = false
          row.setData(row.data)
        }, 5000)
      } else {
        //
        if (row.data._new) {
          // row.data.draft = false
          row.data.draft = true
          row.data._invalid = true
          row.setData(row.data)
          return
        } else {
          row.data.draft = false
          row.data._invalid = true
          row.setData(row.data)
        }
      }
    },

    removeRow(data, index, meta) {
      const grid = this
      let row = this.gridOptions.api.getDisplayedRowAtIndex(index)

      const success = (meta) => grid.removeRow(true, index, meta)
      const failure = (meta) => grid.removeRow(false, index, meta)

      if (data === true) {
        if (this.undo || meta) {
          row.data._deleted = !row.data._deleted
          row.setData(row.data)
        } else {
          this.gridOptions.api.refreshInfiniteCache()
        }

        return
      }

      if (data === false) {
        // row.data._deleted = !row.data._deleted
        // row.setData(row.data)

        this.$notifications.notify({
          type: 'plain',
          icon: 'exclamation-circle',
          placement: { from: 'top', align: 'center' },
          timeout: 0,
          message: `Error: ${meta || '[unspecified]'}. Retry ?`,
          dismiss: 'Yes',
          discard: 'No',
          goto: null,
          onClose: function (dismiss) {
            if (dismiss) {
              //
              !row.data._deleted
                ? grid.$emit('removeRow', { index, data }, success, failure)
                : grid.$emit('restoreRow', { index, data })
            } else if (dismiss === null) {
              //
              row.data._deleted = !row.data._deleted
              row.setData(row.data)
            }
          }
        })

        return
      }

      if (row.data._deleted === true) {
        this.removeRow(true, index, 'undo')
        return
      }

      let _data = data
      let dataOffset = grid.checkboxes ? 3 : 2
      if (meta === 'Id' || data === 'undefined') {
        _data = `
                 ${row.data[grid.columnDefs[dataOffset].field]}
                 ${row.data[grid.columnDefs[dataOffset + 1].field] || ''}
                 ${row.data[grid.columnDefs[dataOffset + 2].field] || ''}
                `
      }

      //
      this.$notifications.notify({
        type: 'plain',
        icon: 'exclamation-circle',
        placement: { from: 'top', align: 'center' },
        timeout: 0,
        message: `Are you sure you want to delete <br/>
                  <em>${grid.label || meta}</em> <strong>${_data}</strong> ?`,
        dismiss: 'Yes',
        discard: 'No',
        goto: null,
        onClose: function (dismiss) {
          if (dismiss) {
            //
            row.setSelected(false)
            if (row.data._new) {
              grid.gridOptions.api.refreshInfiniteCache()
            } else {
              !row.data._deleted
                ? grid.$emit('removeRow', { index, data: row.data }, success, failure)
                : grid.$emit('restoreRow', { index, data: row.data })
            }
          } else if (dismiss === null) {
            //
          }
        }
      })
    },

    removeRows() {
      let toBeDeleted = []
      let newItemsToBeRemoved = 0
      const grid = this
      this.gridOptions.api.forEachNode(node => {
        node.selected && !node.data._new && toBeDeleted.push(node)
        node.selected && node.data._new && newItemsToBeRemoved++
      })

      let errmsg = ''
      if (typeof this.validateRemoveMany !== 'undefined') errmsg = this.validateRemoveMany(toBeDeleted)

      if (errmsg != null && errmsg !== '') {
        this.$notifications.notify({
          type: 'plain',
          icon: 'exclamation-circle',
          placement: { from: 'top', align: 'center' },
          message: errmsg,
          dismiss: 'OK',
          timeout: 0
        })
      } else {
        const success = () => grid.gridOptions.api.refreshInfiniteCache()

        this.$notifications.notify({
          type: 'plain',
          icon: 'exclamation-circle',
          placement: { from: 'top', align: 'center' },
          timeout: 0,
          message: `Are you sure you want to delete ${toBeDeleted.length + newItemsToBeRemoved} items ?`,
          dismiss: 'Yes',
          discard: 'No',
          goto: null,
          onClose: function (dismiss) {
            if (dismiss) {
              //
              toBeDeleted.forEach(row => {
                row.data._deleted = true
                row.setData(row.data)
                row.setSelected(false)
              })
              grid.$emit('removeRows', { rows: toBeDeleted }, success)
            } else if (dismiss === null) {
              //
            }
          }
        })
      }
    },

    rowClass: (grid) => {
      const actionable = grid.model ? grid.model.$$actionable : null
      return {
        'new'    : 'data._new',
        'editing': 'data.draft',
        'pending': 'data._pending',
        'loading': 'data._loading',
        'saved'  : 'data._saved',
        'invalid': 'data.entireRowInvalid || data._invalid',
        'deleted': 'data._deleted',
        'disabled': 'data._disabled',
        'expanded': 'data._expanded',
        'not-actionable': params => typeof actionable === 'function' && !actionable({data: params.data}),
        ...grid.rowClasses
      }
    },

    processRow: (params, grid) => {
      //
      if (!grid.dragRows) return
      if (params.eRow) {
        let $row = params.ePinnedLeftRow
        $row.draggable = true
      }
    },

    onDropRow(srcIdx, destIdx) {
      let from = this.gridOptions.api.getDisplayedRowAtIndex(+srcIdx)
      let to   = this.gridOptions.api.getDisplayedRowAtIndex(+destIdx)
      let fromData = from.data

      from.setData(to.data)
      to.setData(fromData)

      this.$emit('dropRow', { from: +srcIdx, to: +destIdx })
    },

    /**
     * Exporter
     *
     */
    exportList() {
      EventBus.$emit(Eved.GENERIC.EXPORT, this.selectedRows)
      this.gridOptions.api.exportDataAsCsv()
    },

    getTotals() {
      return this.totalsData ? [this.totalsData] : [{}]
    }
  },

  components: {
    AgGridVue,
    ExportData,
    ColumnSelector
  }
}

</script>

<template lang="pug">
.content.grid(:class='"layout-" + layout')

  //- ****** GRID/PAGE TITLE
  .title(v-if='name && layout === "page"')
    ed-title-nav(:title='name', :subtitle='desc' :tabs='tabs', :class='["grid-navigation-bar", $slots["overview"] ? "overview" : ""]')
      slot(v-if='$slots["back-action"]' slot='action' name='back-action')
      span(v-else slot='action')
        router-link(v-if='$routerHistory.hasHistory() && $route.query["back-to"]' :to='{ path: $routerHistory.previous().path, hash: "" }')
          i.fa.fa-long-arrow-left
          span &nbsp; {{ $t('Back') + ' ' + $t('to') + ' ' + $t($route.query['back-to']) }}

  //- ****** DATA SKYBOX
  .container(v-if='$slots["overview"]')
    slot(name='overview')

  //- ****** SEARCH BAR (QUICK SEARCH)
  .search.bar(v-if='!disableFilter')
    .container
      //-.row
        .col-md-12
          .row
            .col-md-6

            .col-md-6.actions-bar(v-if='permissionsMix[$root.currentModule]')
              ed-button.btn.btn-actionbar(v-if='"export" in permissionsMix[$root.currentModule]' @click='exportList')
                | Export
              span
                ed-button.btn.btn-actionbar(
                  v-for='(permission, key) in permissionsMix[$root.currentModule]'
                  :key='key'
                  :to='key'
                  v-if='(permission === null && selectedRows.length || permission) && key != "export"'
                ) {{key}}

  //- ****** FILTERS BAR
  .filter.bar(v-if='!disableFilter')
    .container
      .row
        .col-md-12(:class='{ "no-pad": layout === "frame" }')
            ed-select.filter-views.inline.inline-block.ghost.label-top(
              v-if='layout === "page" && views'
              field-label='Current View'
              hide-optional
              :searchable='false'
              :has-toolbar='true'
              :hide-selected='true'
              track-by='Label'
              :options='savedViews'
              :placeholder='``'
              label='Label'
              v-model='currentView'
              @select='applySavedFilter($event)'
              :endpoint='api.$.common.filterViews'
              open-direction='over'
              :icon='currentView && currentView.icon || "fas fa-filter"'
              title='Saved Views'
              ref='currentViewList'
            )
              span(slot='beforeList' v-if='!backendSavedViews')
                | No saved views
              template(slot='option', slot-scope='props')
                small
                  i(
                    :class='props.option.icon || "action-icon far fa-star option-icon-hover"'
                    @click.stop='setDefaultView(props.option)'
                  )
                | &nbsp; {{ props.option.Label }}
                i.action-icon.pull-right.fas.fa-trash-alt.option-icon-hover(v-if='customViews' @click.stop='removeView(props.option)')
              div(slot='toolbar' v-if='customViews')
                span(v-if='backendSavedViews')
                  label
                    small Save current table as:
                  ed-button.btn.btn-actionbar(
                    :endpoint='api.$.common.filterViews'
                    @click='updateView'
                  )
                    template(v-if='currentView') Save as &lsquo;{{ currentView.Label }}&rsquo;
                    template(v-else) Save view
                ed-input.small.box(
                  v-model='viewToBeSaved.label'
                  placeholder='Save as new view'
                  helptip-header=''
                  :helptip-body='!viewToBeSavedValid && "That name has already been taken." || ""'
                  :error='!viewToBeSavedValid'
                )
                ed-button.btn.btn-actionbar(v-if='currentView && currentView.Label'
                  :endpoint='api.$.common.filterViews'
                  @click='saveView'
                ) Save
            ed-input.filter-views.search-box-.inline.label-top(v-if='layout==="page" && search'
              field-label='Search'
              hide-optional
              v-model='searchTerm'
              @change='quickSearch'
              :placeholder='quickSearchPlaceholder'
              xsearch
              search-box
              ref='quickSearch'
              )
            ed-input.search-box(v-if='layout !== "page" && search'
                v-model='searchTerm'
                @change='quickSearch'
                :placeholder='name ? $t("Search ") + " " + name : $t("Search")'
                search
                ref='quickSearch'
                )
            //-i.separator(v-if='filters.length')
            .form-group.filters-group(v-if='!disableFilter && search && filters.length')
              label(v-if='layout === "page"') &nbsp;
              ul.filters.ghost(:endpoint='api.$.common.optionsFilter')
                li(v-if='filters.length')
                  a.filter.clear(href='#' @click.prevent='removeFilters') clear all filters
                  i.fas.fa-times-circle.del-icon(@click='removeFilters')
                li.filter(v-for='filter in filters' v-show='!~additionalFilters.indexOf(filter.key)')
                  a.filter(
                    href='#'
                    v-tooltip='"Click to edit"'
                    :title='filter.key'
                    @click.prevent='editFilter(filter)'
                  )
                    | {{ filter.column ? `${filter.column}: ` : '' }}{{ filter.label }}
                  i.fas.fa-times-circle.del-icon(v-tooltip='"Click to remove"' @click='removeFilter(filter)')
      .row
        .col-md-12
          slot(name='additionalLabel')
      .row.action-buttons
        .col-md-6.col-xs-12.label-bar
          label(v-if='!search') {{ label }}
          slot(v-else-if='additionalFilters.length' name='additionalFilters' :filters='filters')
          label(v-else) &nbsp;
        .col-md-6.xcol-md-offset-4.col-xs-12.col-xs-offset-0.actions-bar
          slot(name="additionalButtons")
          span(v-if='this.selectedRows.length || this.allRowsSelected')
            slot(name="selectionButtons")
          ed-button.btn.btn-actionbar(
              v-if='removeMany && (this.selectedRows.length || this.allRowsSelected)'
              @click='removeRows'
            ) Delete
          ed-button.btn.btn-actionbar(
              v-if='cloneMany && (this.selectedRows.length || this.allRowsSelected)'
              @click='cloneRows'
            ) Duplicate
          | &nbsp;
          export-data(v-if='allowExport' :endpoint='exportUri')
          column-selector(
            v-if='this.showColumnSelector'
            :columns='this.columnDefs'
            :api='gridOptions.columnApi'
            )

  //- ****** DATA GRID
  .row.grid
    .col-md-12
      .grid-wrap.loading-top.list(:class='{ "loading": loading || pendingRequests > 0 }')
        ag-grid-vue.grid.ag-fresh.list(:endpoint='endpoint' :style='getSize',
          :gridOptions='gridOptions'
          :enableColResize='resizing'
          :class=`{
            "checkboxes": checkboxes,
            "all-rows-selected": !!allRowsSelected,
            "draggable": !!dragRows,
            "editable": !!editable,
            "editable-on-add": !!editFullRowOnAdd,
            "shown-details": shownDetails,
            "popover-open": shownPopover && data,
            "no-results-overlay": data,
            "no-select-all": noResults() || hideSelectAll
          }`
          ref='grid'
          )
        .bottom-bar(v-if='footer')
          ed-button.refresh-button.btn.btn-actionbar(@click='quickSearch') Reload
          span {{ $t('Number of records') }}: {{ noOfRecords }}
      ed-input-tooltip(v-if='totalsInvalid && data.length && enableErrorMessage'
        :class='{ "error-message": totalsInvalid }'
        :helptipHeader='helptipHeader'
        :helptipBody='helptipBody'
      )
      ed-input-tooltip.error-message.error.cell-error(v-if='canShowNotifications' v-for='(notification, i) in Notifications' :key='`${i}+${resetNotifications}`'
        v-show='canShowNotifications && !notification.hide && notification.type !== "info"'
        helptipHeader=' '
        helptipBody=' '
        :style='{ top: (notification.position || {}).offsetTop }'
      )
        span(slot='message')
          component(:is='notification.component')
      //.cloaker
</template>

<style lang='scss'>
@import "~@/assets/sass/_variables.scss";
@import "~@/assets/sass/font-awesome/_variables.scss";
@import "~@/assets/sass/_form_fields.scss";

.ag-cell {
  cursor: default;
 }

.ag-selection-checkbox:not(.ag-hidden) + .ag-cell-value {
  cursor: pointer;
}

.refresh-button.refresh-button[class] {
  float: right;
  margin: -3px 0 0 0;
}

//
.ag-row {
  line-height: 22px;
}
.row-ghost {
  background: #faffaa;
  border: 1px solid #ffdd00;
  color: #000;
  font-size: $font-size-base;
  font-weight: $font-weight-bold;

  .ag-cell {
    padding-left: 2em;
  }
  .ag-row-expand-icon {
    display: none;
  }
}

.cloaker {
  height: 10px;
  background: red;
  position: relative;
  z-index: 2;
}
.grid-wrap {
  //margin-bottom: -10px;

  &.loading {
    .ag-body-container .ag-row {
      &:hover {
        overflow: hidden;
      }
    }
    .ag-body-container .ag-cell {
      .ag-custom-cell.ag-cell-money {
        .money.nil {
          opacity: 1;
          @include theme() { background: v(background-lightest) }

          .formatted {
            visibility: hidden;
          }
        }
        .ag-custom-cell-value {
          visibility: visible;
        }
      }

      .ag-custom-cell {
        .ag-detail-cell-value,
        .ag-row-expand-icon,
        .ag-custom-cell-value {
          visibility: hidden;
        }

        &:before {
          content: ' ';
          display: block;
          @include theme() { background: v(background-lightest) }
          height: $font-size-large;
          margin-top: 2px;
        }
      }

      &:after {
        content: ' ';
        display: block;
        height: $font-size-small;
        width: 90%;
        margin-top: 5px;
      }
      &:nth-child(2n+1):after {
        @include theme() { background: v(background-lightest) }
      }
      &:nth-child(2n+2):after {
        @include theme() { background: v(background) }
      }
      &:nth-child(2n+3):after {
        @include theme() { background: v(background-light) }
      }

    }
  }
}
.grid.ag-fresh {
  //transition: height 0.3s ease-out;
  &, .ag-body, .ag-floating-bottom, .ag-floating-bottom .ag-row {
    @include theme {
    background: v(canvas);
    }
  }

  // TOTALS
  .ag-floating-bottom {
    .ag-custom-cell .ag-row-expand-icon {
      display: none;
    }
    .ag-cell-no-focus,
    .ag-cell-focus {
      border: none;
      line-height: 1.8em;
    }
    font-weight: $font-weight-bold;
    font-size: $font-size-base;
    .ag-cell.ag-cell.ag-cell.ag-cell.ag-cell.ag-cell {
      border-bottom: none;
      transform: rotate(0.03deg); // soften font a bit
      .invalid, invalid-threshold {
        @include theme {
          color: v(urgent-01);
        }
      }
    }
    &:before {
      transform: rotate(0.03deg); // soften font a bit
      content: "TOTALS";
      position: absolute;
      line-height: 2.8em;
      padding-left: 0.5em;
      z-index: 99;
      @include theme {
        //background: lighten(v(background-light), 1%);
        background: v(canvas);
      }
    }
    .ag-cell-money {
      &, .ag-custom-cell-value {
        display: block;
      }
    }
  }

  &.shown-details {
    .ag-body-viewport {
      overflow: visible !important;
    }

    .ps__rail-x {
      display: none;
    }
  }

  &.no-results-overlay {
    .ag-bl-overlay {
      display: none;
    }
  }

  &.no-select-all {
    .ag-header-select-all, .ag-custom-header .from-header.has-tooltip {
      display: none;
    }
  }

  .ag-cell {
    max-height: 35px;

    @at-root .theme .grid .ag-body .ag-row .ag-cell.ag-cell-not-inline-editing.ag-cell-no-focus[col-id="details"] {
      padding: 0;
      background: none;
    }
  }
}

.ag-cell.ag-cell-not-inline-editing:not(.ag-cell-expanded) {
  border-top: none;
  border-left: none;
  border-bottom: none;
  //border: 1px dashed transparent;
  @include theme {
  //border-right: 1px solid v(background-light);
  }

  &.ag-cell-focus {
    border-right: none;
    @include theme {
    border-bottom: 1px solid v(background-light);
    }
  }
}

.ag-pinned-left-cols-container .ag-cell.ag-cell-not-inline-editing:not(.ag-cell-expanded) {
  &.ag-cell-focus {
    @include theme {
    border-right: 1px solid v(background-light);
    }
  }
}
.ag-body-container .ag-row:not(.ag-row-selected) .ag-cell.ag-cell-not-inline-editing:not(.ag-cell-expanded) {
  &.ag-cell-focus:not([col-id^="action"]) {
    border-right: none;
    @include theme {
    background-color: v(background-lightest);
    }
  }
}

.ag-body-viewport {
  position: relative; // required for perfect-scrollbar
  .ps__rail-y {
    margin-right: 5px;
    z-index: 99;
  }
  .ps__rail-x {
    z-index: 99;
  }
}

.ag-pinned-right-cols-viewport[style*="display: inline"] + .ag-body-viewport-wrapper .ag-body-viewport .ps__rail-y {
  display: none;
}
.ag-pinned-right-cols-viewport[style*="display: inline"] + .ag-body-viewport-wrapper .ag-body-viewport .ag-filter .ps__rail-y {
  display: block;
}
.ag-pinned-right-cols-viewport.ps .ps__rail-y {
  top: 0 !important;
  right: 1px;
}
.ag-pinned-right-cols-container {
  margin-bottom: 0 !important;
  width: 100% !important;
}

.ag-pinned-left-cols-container,
.ag-pinned-right-cols-container {
  .ag-row.loading:before {
    display: none;
  }
}

.ag-header-cell[col-id="details"],
.ag-fresh .ag-cell.ag-cell[col-id="details"] {
  border: none;
  .ag-row-expand-icon {
    visibility: hidden;
  }
}

.ag-cell.action-view {
  .btn {
    margin: 0;
  }
}

.ag-cell[col-id="action-drag"] {
  padding-left: 0;
}
.ag-cell .drag-handle {
  @include drag-handle;
  cursor: pointer;
}

.ag-fresh .ag-ltr .ag-pinned-right-header {
  border: none;
}
.ag-pinned-right-cols-viewport {
  //width: 1px !important;
}
.ag-body-viewport-wrapper {
  //margin-right: 0 !important;
}

/* .ag-pinned-left-cols-container {
  .ag-row.not-actionable {
    .ag-selection-checkbox {
      display: none;
    }
  }
} */
.ag-body-container {
  //margin-left: -10px;
  .ag-row.not-actionable {
    /* .ag-selection-checkbox {
      display: none;
    } */
    .ag-cell[col-id*="more-actions"], .ag-cell[col-id*="details"] {
      display: none;
    }
    &.ag-row-hover {
      .ag-cell[col-id^="action"] {
        width: 0 !important;
      }
    }
  }
  .ag-row {
    &.ag-row-hover, &:hover {
      .ag-cell[col-id^="action"] {
        width: 35px !important;
        border: none;
        position: relative;
        left: auto !important;
        float: right;

        a[data-action], .ag-cell-wrapper {
          opacity: 1;
          visibility: visible;
        }
      }

      .ag-cell.hover-action {
        opacity: 1;
        visibility: visible;
      }
    }
    .ag-cell.hover-action {
      padding: 5px;
      visibility: hidden;
      opacity: 0;
      transition: opacity .3s ease-in-out;
    }

    .ag-cell[col-id^="action"] {
      @include theme {
        background: inherit;
        border-bottom: 1px solid v(background-light);
        // z-index: 2;
      }
      //width: 0px !important;
      padding-left: 0;
      padding-right: 0;

      a[data-action], .ag-cell-wrapper {
        visibility: hidden;
        opacity: 0;
        transition: opacity .3s ease-in-out;
      }
    }

    .ag-cell[col-id*="more-action"] {
      width: 35px !important;
      border: none;
      position: relative;
      left: auto !important;
      float: right;

      padding-top: 3px;
      overflow: visible;

      a[data-action], .ag-cell-wrapper {
        visibility: visible;
        opacity: 1;
      }
    }
  }
}

.ag-cell {
  &.amount {
    text-align: right;
    & {
      .ag-cell-value {
        //padding-right: 12px;
      }
    }
    .ag-cell-edit-input {
      text-align: right;
      padding-right: 12px;
    }
  }
}

// uri
.ag-cell a[data-action="goto"] {
  //font-weight: $font-weight-bold;
  font-size: $font-size-base-sm;
  transform: rotate(0.03deg); // soften font a bit
  text-decoration: underline;
  @include theme {
    color: #4A90E2; // saturate(v(primary-04), 35%);
  }
  display: inline;
  &.nogo {
    text-decoration: none;
    cursor: default;
  }
  &.with-badge {
    max-width: 80%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}

.ag-pinned-right-cols-container .ag-cell[col-id^="action"] {
  @include theme {
    //border-left: 1px solid v(background-light);
  }
}
.ag-cell[col-id^="action"] {
  line-height: 2.1em;
  text-align: center;
  a:not(.btn) {
    cursor: pointer;
    font-size: $font-size-base;
    @include theme {
    color: v(border);
    &:hover {
      color: v(ink);
    }
    }

    .ag-row.deleted &[data-action="remove"] {
      &:before {
        @include fa-icon-in($fa-var-undo);
      }
      @include theme {
      color: v(urgent-02);
      }
      position: relative;
      z-index: 99;
    }

    .ag-row.expanded &[data-action="expand"] {
      &:before {
        @include fa-icon-in($fa-var-chevron-up);
      }
    }
  }

  &.ag-cell-focus {
    @include theme {
    border-color: v(background-light);
    }
    height: 100%;
  }
}

body .dawn-modal-container .grid.content.layout-page {
  > .title,
  > .filter.bar,
  > .search.bar {
    margin-left: auto;
    margin-right: auto;
  }
  > .title {
    .dawn-title-nav,
    .container {
      padding: 0;
    }
  }
}

//
.grid.popover-open {
  .ag-bl-full-height,
  .ag-root,
  .ag-body-viewport {
    overflow: visible !important;
  }
  .ag-floating-bottom {
    @include theme {
      border-bottom: 1px solid v(background);
    }
  }
}

.content.grid {
  .row.grid {
    margin-top: 20px;
    padding-left: 15px;
    padding-right: 15px;

    .dawn-input-tooltip {
      top: auto;
      bottom: -0.75em;
      left: calc(100% + 15px);
      max-width: 250px;

      &.error {
        bottom: auto;
      }
      &.cell-error {
        visibility: visible;
      }
    }
  }

  .separator {
    @include theme { color: v(background-light) }
    padding-left: 10px;
    padding-right: 10px;
    &:before {
      content: '|';
    }
  }

  // TITLE NAV
  .title {
    margin-left: -15px;
    margin-right: -15px;
  }
  .dawn-modal-header .title {
    margin-left: 0;
  }
  .grid-navigation-bar {
    &.overview .dawn-title-nav {
      border-bottom: none;
    }
  }

  //
  & ~ .content &, & ~ .row .content & {
    .dawn-title-nav {
      border: none;
      .action-area {
        display: none;
      }
    }
    &.layout-frame {
      border-top: none;
    }
  }

  // LAYOUTS
  &.layout {
    &-widget {
      margin: -.3rem;

      > .title,
      .search.bar,
      .filter.bar {
        display: none;
      }

      .column-selector.dropdown {
        right: 0;
        display: none;
      }

      .grid-wrap {
        margin-left: -30px;
        margin-right: -30px;

        &.loading:before, .loading:before {
          font-size: 22px;
          margin-top: -11px;
          margin-left: -11px;
        }
      }

      .ag-fresh .ag-ltr .ag-header-cell {
        padding-top: 1px;
      }
    }

    &-page {
      @at-root .theme.skin & {
        // padding-top: 0;
      }

      .search.bar {
        // margin-top: 2px;
        @include theme {
        background: linear-gradient(
                      darken(v(background-lightest), 8%),
                             v(background-lightest)
                             10%
                    );
        }
        //
        margin-left: -15px;
        margin-right: -15px;
      }
      .search-box {
        margin-top: 10px;
        margin-bottom: 10px;
      }
      .filter.bar {
        @include theme {
        background: v(background-lightest);
        border-bottom: 1px solid darken(v(background-light), 5%);
        box-shadow: 0 2px 4px 0 v(background-light);
        }
        //
        margin-left: -15px;
        margin-right: -15px;

        padding-top: 8px;
        padding-bottom: 4px;
      }

      .ag-header {
        @include theme {
        //border-left: 1px solid lighten(v(background), 5%);
        }
      }

      // fit page on small screens
      @include media('<tablet') {
        .dawn-title-nav .container,
        .search.bar .container,
        .filter.bar .container {
          margin-left: -25px;
          margin-right: -25px;
        }
        .row.grid {
          padding-left: 0;
          padding-right: 0;
          margin-left: -15px;
          margin-right: -15px;
          [class*="col-md"] {
            padding-left: 0;
            padding-right: 0;
          }
          .column-selector.dropdown {
            right: 0;
          }
        }
      }
    }

    &-frame {
      @include theme {
      border: 1px solid v(background);
      }
      .grid-wrap {
        margin-left: -15px;
        margin-right: -15px;
      }

      .ag-fresh .ag-menu {
        margin-top: 0;
        top: 0 !important;
      }

      .ag-row:not(.ag-row-editing) .ag-cell.ag-cell-no-focus {
        @include theme {
        border-bottom: 1px solid v(background-light);
        }
      }
      .ag-cell.ag-cell-focus {
        //margin-top: -1px;
        //margin-left: -1px;
        //line-height: 1.5em;
        //height: calc(100% + 1px);
      }

      .row.grid {
        margin-top: 0;
        padding-left: 0;
        padding-right: 0;
        //margin-left: -15px;
        //margin-right: -15px;
        .column-selector.dropdown {
          right: 0;
        }
        &.row.row {
          margin-left: 0;
          margin-right: 0;
        }
      }

      .filter.bar {
        @include theme {
        background: v(canvas);
        border-top: 1px solid v(background-light);
        }
        padding: 0 15px;

        > .container {
          padding: 0;
          //padding-left: 15px;

          > .row {
            @include theme {
            border-bottom: 1px solid v(background-light);
            }
            margin-left: -15px;
            margin-right: -15px;
          }
        }
        .separator {
          display: none;
        }
      }
      .search.bar {
        margin: -1px;

        @include theme {
        background: v(canvas);
        border: 1px solid v(background);
        }

        .container {
          padding: 0;
        }
      }
      .search-box {
        margin-top: 5px;
        margin-left: 15px;
        margin-bottom: 10px;
        padding-bottom: 0;

        .form-control {
          font-size: $font-size-small;
          @include placeholder {
            font-size: $font-size-small;
            color: $background;
          }
          &+.search-icon {
            font-size: $font-size-small;
          }
        }
      }
    }

    @at-root .dawn-modal-container .grid.layout-page,
    &-small {
      .search.bar {
        margin-top: 5px;
        @include theme {
        background: v(canvas);
        border: 1px solid v(background-light);
        }
      }
      .search-box {
        margin-top: 0;
        padding-bottom: 0;

        .form-control {
          font-size: $font-size-small;
          @include placeholder {
            font-size: $font-size-small;
            color: $background;
          }
          &+.search-icon {
            font-size: $font-size-small;
          }
        }
      }
      .filter.bar {
        @include theme {
        background: v(canvas);
        border-top: 1px solid v(background);
        }
      }
    }

  }

  .filter-views {
    z-index: 9999;
  }

  // SEARCH BAR
  .search-box, .search-box .form-control {
    font-size: $font-size-large;
    &, &:focus {
      border: none;
    }

    @include placeholder {
      font-size: $font-size-large;
      color: $background;
    }
    &+.search-icon {
      font-size: $font-size-large;
    }
  }

  // ACTIONS
  .actions-bar {
    text-align: right;
    line-height: 2em;
  }

  .form-group {
    > label {
      font-weight: $font-weight-extra-bold;
    }
  }

  // FILTERS
  @mixin filter-tag {
    padding: 3px 5px 3px 8px;
    line-height: 1.2em;
    border-radius: $border-radius-label;
    @include theme {
    background: v(canvas);
    border: 1px solid lighten(v(background), 8%);
    }

    &.from-view {
      position: relative;
      margin-right: .95em;
    }
    &.from-view:after {
      content: '';
      display: inline-block;
      width: 1px;
      height: 1.6em;
      @include theme {
      border-right: 1px solid lighten(v(background), 8%);
      }
      height: 1.6em;
      position: absolute;
      right: -15px;
      top: 0;
    }
  }

  .filters-group {
    display: inline-block;
    margin-bottom: 0;
    > label {
      display: block;
    }
  }
  ul.filters {
    display: inline-block;
    margin-bottom: 0;
    font-size: $font-size-small;
    line-height: 2.5em;

    .from-view + .from-view {
      margin-left: 1px;
      &:before {
        content: '';
        display: inline-block;
        width: 1px;
        height: 1.6em;
        @include theme {
        border-right: 1px solid v(background-lightest);
        }
        height: 1.6em;
        position: absolute;
        top: 0;
        left: -2px;
      }
    }

    &, li {
      list-style-type: none;
      padding: 0;

      li {
        @include filter-tag;
        display: inline-block;
        margin-left: .95em;

        .filter {
          font-size: 0.95em;
          text-decoration: none;

          &.clear {
            text-decoration: none;
          }
        }

        .del-icon {
          cursor: pointer;

          @include theme {
          color: darken(v(ink-light), 10%);
          }

          &:hover {
            @include theme {
            color: v(ink);
            }
          }
          margin-left: 7px;
          vertical-align: middle;
        }
      }
    }
  }

  // COLUMN HEADERS
  .ag-fresh .ag-ltr .ag-header-cell {
    padding-top: 12px;
    padding-left: 0px;
    font-size: $font-size-base-sm;
    transform: rotate(.03deg); // soften font a bit
    border: 0;
    text-indent: -1px;

    .ag-cell-label-container {
      .ag-header-cell-text {
        padding-left: 9px;
      }
      .ag-header-cell-menu-button:hover + .ag-header-cell-label .ag-header-cell-text {
        text-decoration: underline;
      }
    }

    .ag-header-cell-menu-button {
      // trigger menu on clicking on entire cell
      // hide default menu icon
      position: absolute;
      width: 100%;
      cursor: pointer;
      transition: none !important; // disabled to play nice with font smoothing

      .ag-icon-menu {
        visibility: hidden;
      }
    }
  }

  // DROPDOWN FILTERS
  .ag-fresh {
    .ag-root {
      @include theme {
      border-top-color: v(background-light);
      }
    }
    .ag-header {
      @include theme {
      border-bottom-color: v(background-light);
      }
    }

    .ag-menu {
      margin-top: -33px;
      //margin-left: -1px;
      box-shadow: $box-shadow;
      z-index: 9999;
    }
    .ag-filter .grid-filter {
      //margin-left: -1px;
    }
    .ag-menu, .ag-filter {
      @include theme {
      background: v(canvas);
      //border-color: $gray-light;
      border-color: v(background-light);
      }
      border-radius: 0 0 $border-radius-large $border-radius-large;
      transition: opacity .3s ease-in-out;
      opacity: 0;
      margin-left: -1px;

      &.fade.in,
      &.fade.in .ag-filter {
        opacity: 1;
      }

      @at-root .column.selector,
      .ag-filter {
        padding: 7px 8px 10px;

        .column-title {
          font-weight: 700;
          font-size: $font-size-base-sm;
          transform: rotate(.03deg); //soften font a bit
          margin-top: 7px;
        }
        @at-root .column.selector .column-title {
          line-height: normal;
          font-size: $font-size-xsmall;
          text-transform: uppercase;
        }

        .ag-filter-select {
          margin: 0;
          display: none;
        }
        .ag-filter-filter {
          border: none;
        }
        .ag-filter-apply-panel {
          padding: 0;
          border: none;
          margin-top: 5px;
        }
      }

      @include placeholder {
        font-size: $font-size-base-sm;
      }

      .datepicker {
        .navigator {
          margin-bottom: 0;
          padding-bottom: 0;
        }
        .calendar {
          padding-top: 0;
          padding-bottom: 0;
        }
        .range-links-wrapper {
          top: -39px;
          bottom: -49px;
          right: -7px;
          border-radius: 0 0 6px 0;
        }
      }

      .form-group {
        margin-top: 0;
        line-height: 1.7rem;

        .form-check {
          line-height: 1.5rem;
          margin-left: -5px;
          padding-left: 10px;

          &:hover, &.checked {
            cursor: pointer;
            @include theme {
            background-color: lighten(v(secondary-04), 7%);
            }
          }
        }

        .theme.skin & .form-control[search][class] {
          font-size: $font-size-base-sm;
          padding-top: 0;
          padding-bottom: 0;
          padding-left: 20px;
          height: 30px;
        }

        .form-control[search]::-webkit-input-placeholder { font-size: $font-size-base-xs; text-transform: uppercase; }
        .form-control[search]:-moz-placeholder           { font-size: $font-size-base-xs; text-transform: uppercase; }
        .form-control[search]::-moz-placeholder          { font-size: $font-size-base-xs; text-transform: uppercase; }
        .form-control[search]:-ms-input-placeholder      { font-size: $font-size-base-xs; text-transform: uppercase; }
      }

      @at-root .column.selector .form-group {
        margin-top: 0;
      }
      @at-root .column.selector .btn,
      & .btn {
        margin-top: 0;
        margin-bottom: 0;
      }
    }
  }

}


// INLINE EDIT
.grid .ag-fresh .ag-row:not(.ag-row-editing) .ag-cell.ag-cell-no-focus.ag-cell-inline-editing,
.ag-row:not(.ag-row-editing) .ag-cell-inline-editing {
  @include theme {
  border: 2px solid v(tertiary-01);
  }
  .ag-cell-edit-input {
    border: none;
    padding-left: 4px;
  }
}
.editable-on-add .ag-row-editing.new .ag-cell {
  @include theme {
  border: 1px solid v(background-light);
  border-left: none;
  border-bottom: 2px solid v(tertiary-01);
  }
  .ag-cell-edit-input {
    border: none;
  }
}
.grid {
  .ag-body-container .ag-row:not(.disabled) .ag-cell.editable {
    &.amount.text-right {
      .ag-cell-wrapper .ag-cell-value {
        padding-right: 10px;
      }
    }
  }
  .ag-cell.amount {
    .ag-cell-wrapper {
      max-height: 2.2em;
    }
  }
  .ag-body-container .ag-row:not(.disabled) .ag-cell.editable {
    .ag-cell-wrapper {
      margin-top: -2px;
      padding: 0 0 0 5px;
      display: block;
      @include theme {
        border: 1px solid v(background);
      }
      min-height: 2.2em;
    }
    > .ag-custom-cell {
      > .ag-custom-cell-value {
        margin-top: -2px;
        padding: 0 0 0 5px;
        display: block;
        @include theme {
          border: 1px solid v(background);
        }
        min-height: 2.2em;
        padding-right: 7px;
      }
      &.invalid-amount > .ag-custom-cell-value {
        @include theme {
          border: 1px solid v(urgent-01);
        }
      }
    }

    &:hover {
      .ag-cell-wrapper {
        cursor: pointer;
        @include theme {
          border: 1px solid v(ink);
        }
      }
      > .ag-custom-cell {
        > .ag-custom-cell-value {
          cursor: pointer;
          @include theme {
            border: 1px solid v(ink);
          }
        }
        &.invalid-amount > .ag-custom-cell-value {
          @include theme {
            border: 1px solid v(urgent-01);
          }
        }
      }
    }
  }
}

.ag-selection-checkbox.ag-hidden + .ag-cell-value .ag-custom-cell.ag-custom-cell {
  min-width: 95%;
  max-width: 95%;
}

.ag-row:not(.ag-row-selected) {
  transition: background .3s ease-in-out;

  &:after {
    content: '';
    opacity: 0;
    transition: opacity .3s ease-in-out,
                background .3s ease-in-out;
  }

  &.drag-enter, &.drag-enter .ag-cell.disabled {
    @include theme {
    box-shadow: inset 0 2px 0 0 v(secondary-01);
    }
    .ag-cell-wrapper {
      pointer-events: none;
    }
  }
  &.drag-over {
    //opacity: .3;
  }

  &.new {
    @include theme {
    //color: v(border);
    background-color: lighten(v(secondary-01), 50%);
    }
  }

  &.editing:not(.ag-row-editing) {
    @include theme {
    background-color: lighten(v(attention-03), 10%);
    }
  }

  &.pending {
    @include theme {
    //background-color: lighten(v(urgent-02), 10%);
    }
    &:after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0.8;
      @include theme {
      background-color: v(canvas);
      border-bottom: 1px solid v(background-light);
      }
      z-index: 1000;
    }
    &:before {
      left: 20px;
      font-size: 22px;
      margin-top: -11px;
      margin-left: -11px;
    }
  }

  &.saved {
    @include theme {
    background-color: lighten(v(tertiary-01), 20%);
    }
  }

  &.invalid {
    @include theme {
    background-color: lighten(v(urgent-02), 30%);
    }
    .ag-cell[col-id^="action"] {
      a:hover {
        @include theme {
        color: v(ink);
        }
      }
    }
  }

  &.deleted {
    @include theme {
    //background-color: lighten(v(urgent-02), 10%);
    }
    &:after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0.8;
      @include theme {
      background-color: v(canvas);
      border-bottom: 1px solid v(background-light);
      }
    }
  }
}

.ag-body-viewport-wrapper .ag-row:not(.ag-row-selected) {
  &.disabled .ag-cell:not(.ag-cell-expanded),
  & .ag-cell.disabled {
    //pointer-events: none;
    //opacity: .5;
    /*@include theme {
    color: v(ink);
    background: v(background-lightest);
    }*/
  }
}


// ICONS
.ag-icon.ag-icon-filter {
  display: none;
  @include fa-icon($fa-var-filter);
}
.ag-icon.ag-icon-asc {
  @include fa-icon($fa-var-sort-up);
}
.ag-icon.ag-icon-desc {
  @include fa-icon($fa-var-sort-down);
}
.ag-icon.ag-icon-none {
  @include fa-icon($fa-var-sort);
}

.ag-header .ag-icon.ag-icon-checkbox {
  &-unchecked, &-checked {
    //margin-left: 1px;
  }
}
.ag-icon.ag-icon-checkbox {
  &-checked,
  &-unchecked {
    background-image: none;
    @include form-field;
    @include dawn-checkbox;
    &.ag-icon {
      width: 0.95rem;
      height: 0.95rem;
      border-radius: 2px;
      @include theme { border-color: v(border) }
      &:hover, &.hover {
        @include theme { border-color: v(ink) }
      }
    }
  }
  &-unchecked {
    //
  }
  &-checked {
    opacity: 1;
    @include theme { color: v(secondary-01) }
  }
}

// Show selectAll on first column
.grid-wrap .checkboxes:not(.no-select-all) {
  &.ag-fresh:not(.draggable) {
    .ag-pinned-left-header[style*="display: none"] + .ag-pinned-right-header + .ag-header-viewport .ag-header-cell[style*="left: 0"] .ag-header-select-all.ag-hidden,
    .ag-pinned-left-header[style*="display: inline-block"] .ag-header-cell[style*="left: 0"] .ag-header-select-all.ag-hidden {
      display: inline-block !important;
      margin-left: 7px;
    }
  }
  &.ag-fresh:not(.all-rows-selected) .ag-header-cell[style*="left: 0"] .ag-header-select-all.ag-hidden .ag-checkbox-checked,
  &.ag-fresh.all-rows-selected .ag-header-cell[style*="left: 0"] .ag-header-select-all.ag-hidden .ag-checkbox-unchecked {
    display: none !important;
  }
  &.ag-fresh:not(.all-rows-selected) .ag-header-cell[style*="left: 0"] .ag-header-select-all.ag-hidden .ag-checkbox-unchecked,
  &.ag-fresh.all-rows-selected .ag-header-cell[style*="left: 0"] .ag-header-select-all.ag-hidden .ag-checkbox-checked.ag-hidden {
    display: inline-block !important;
  }
  &.ag-fresh.ag-fresh .ag-header-cell[style*="left: 0"] {
    z-index: 2;
  }

  .ag-pinned-left-header[style*="display: none"] + .ag-pinned-right-header + .ag-header-viewport .ag-header-cell[style*="left: 0"] .ag-header-select-all.ag-hidden,
  .ag-pinned-left-header[style*="display: inline-block"] .ag-header-cell[style*="left: 0"] .ag-header-select-all.ag-hidden {
    display: inline-block !important;
    margin-left: 21px;
    .ag-checkbox-checked {
      margin-left: -1px;
    }
  }
}

.content.grid .ag-fresh .ag-ltr .ag-header-cell[style*="left: 0"] .ag-header-cell-menu-button,
.content.grid .ag-fresh .ag-ltr .ag-header-cell[style*="left: 0"] .ag-header-cell-menu-button {
  width: calc(100% - 30px);
}

// No datas
.ag-overlay-no-rows-wrapper {
  vertical-align: top;
  padding-top: 15%;

  @include theme {
  color: v(border);
  }
  font-size: $font-size-base-lg;
  .icon {
    font-size: 40px;
  }
  p {
    margin-top: 10px;
  }
}

// Footer
.bottom-bar {
  padding: .8em;
  font-size: $font-size-xsmall;
  @include theme {
  color: v(ink);
  background: v(background-lightest);
  border: 1px solid v(background-light);
  border-top: none;
  }
  margin-top: -1px;
  position: relative;
}

</style>



// WEBPACK FOOTER //
// src/components/Grid/index.vue